import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Button,
  Box,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Logout as LogoutIcon,
  MarkEmailRead as MarkEmailReadIcon,
} from "@mui/icons-material";
import { Chip, CircularProgress, Tooltip } from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RefreshIcon from "@mui/icons-material/Refresh";

const Header = ({ setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
const [hasMoreNotifications, setHasMoreNotifications] = useState(false);
const hasMoreRef = React.useRef(false);
const [notifPageNo, setNotifPageNo] = useState(1);
const notifPageNoRef = React.useRef(1);
const [loadingMoreNotifs, setLoadingMoreNotifs] = useState(false);
const [markingAll, setMarkingAll] = useState(false);
const notifListRef = React.useRef(null);
const intervalRef = React.useRef(null);
const apiUrl = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const fetchNotifications = async (pageNo = 1, append = false) => {
  try {
    const response = await axios.get(
      `${apiUrl}/NewNotification/GetMyNotifications?PageNo=${pageNo}&PageSize=20`,
      { headers: { Authorization: token } }
    );
    const items = Array.isArray(response.data?.data) ? response.data.data : [];
    setNotifications(prev => append ? [...prev, ...items] : items);
    setHasMoreNotifications(items.length === 20);
    hasMoreRef.current = items.length === 20;
  } catch (error) {
    console.error("Error fetching notifications:", error);
  }
};

const fetchUnreadCount = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/NewNotification/GetUnreadCount`,
      { headers: { Authorization: token } }
    );
    setUnreadCount(typeof response.data?.unreadCount === 'number' ? response.data.unreadCount : 0);
  } catch (error) {
    console.error("Error fetching unread count:", error);
  }
};

useEffect(() => {
  fetchUnreadCount();
  intervalRef.current = setInterval(fetchUnreadCount, 30000);
  return () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };
}, []);

  // Handle notification dropdown
 const handleNotificationClick = (event) => {
  setAnchorEl(event.currentTarget);
  setNotifPageNo(1);
  notifPageNoRef.current = 1;
  fetchNotifications(1, false);
};

  const handleCloseNotifications = () => {
    setAnchorEl(null);
  };

const handleMarkAsRead = async (recipientId) => {
  setNotifications(prev =>
    prev.map(n => n.recipientId === recipientId ? { ...n, isRead: true } : n)
  );
  setUnreadCount(prev => Math.max(0, prev - 1));
  try {
    await axios.post(
      `${apiUrl}/NewNotification/MarkAsRead`,
      { recipientId },
      { headers: { Authorization: token } }
    );
  } catch (error) {
    setNotifications(prev =>
      prev.map(n => n.recipientId === recipientId ? { ...n, isRead: false } : n)
    );
    setUnreadCount(prev => prev + 1);
    console.error("Error marking notification as read:", error);
  }
};

const markAllAsRead = async () => {
  setMarkingAll(true);
  try {
    await axios.post(
      `${apiUrl}/NewNotification/MarkAllAsRead`,
      {},
      { headers: { Authorization: token } }
    );
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  } catch (error) {
    console.error("Error marking all as read:", error);
  } finally {
    setMarkingAll(false);
  }
}; 

  // Logout dialog
  const handleLogout = () => {
    setOpenLogoutDialog(true);
  };

  const handleConfirmLogout = () => {
    sessionStorage.clear();
    navigate("/");
    setOpenLogoutDialog(false);
  };

  const handleCancelLogout = () => {
    setOpenLogoutDialog(false);
  };

  const open = Boolean(anchorEl);
  const id = open ? "notification-popover" : undefined;

  const organizationName =
  (sessionStorage.getItem("organizationName") || "").replace(/^"|"$/g, "");

const logo = sessionStorage.getItem('clientLogo');

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: "linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        height: "70px",
        zIndex: 1300,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Sidebar Toggle Button (Mobile) */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={() => setIsSidebarOpen((prev) => !prev)}
          sx={{
            display: { md: "none" },
            mr: 2,
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo and Institute Name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
  src={logo ? `https://wsmsv1.aidigitalsec.com${logo.replace('~', '')}` : undefined}
  alt="School Logo"
  sx={{
    width: 48,
    height: 48,
    border: "2px solid rgba(255, 255, 255, 0.3)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  }}
/>
          <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.8,
                color: "#ffffff",
                fontSize: "1.1rem",
                lineHeight: 1.2,
                textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
              }}
            >
            {organizationName || "Unknown"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.85)",
                fontSize: "0.75rem",
                fontWeight: 500,
                letterSpacing: 1.2,
                textTransform: "uppercase",
              }}
            >
              Parents Panel
            </Typography>
          </Box>
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right Side Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Notifications Icon */}
          <IconButton
            color="inherit"
            onClick={handleNotificationClick}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
              transition: "all 0.3s ease",
            }}
          >
          <Badge
  badgeContent={unreadCount}
  color="error"
  sx={{
    "& .MuiBadge-badge": {
      fontSize: "0.75rem",
      minWidth: "24px",
      height: "24px",
      borderRadius: "50%",
      fontWeight: 700,
      boxShadow: "0 4px 8px rgba(211, 47, 47, 0.3)",
      background: "linear-gradient(135deg, #ff4757 0%, #ff3838 100%)",
      color: "#fff",
      border: "2px solid #fff",
    },
  }}
>
  <NotificationsIcon sx={{ fontSize: "1.3rem" }} />
</Badge>
          </IconButton>

          {/* Logout Button */}
          <Button
            variant="contained"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              ml: 1,
              textTransform: "none",
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              color: "#ffffff",
              fontWeight: 600,
              fontSize: "0.9rem",
              padding: "8px 20px",
              borderRadius: "8px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              "&:hover": {
                background: "rgba(211, 47, 47, 0.9)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Logout
          </Button>
        </Box>
      </Toolbar>

     {/* Notification Popover */}
<Popover
  id={id}
  open={open}
  anchorEl={anchorEl}
  onClose={handleCloseNotifications}
  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
  transformOrigin={{ vertical: "top", horizontal: "right" }}
  PaperProps={{
    sx: {
      width: 420,
      maxHeight: 500,
      borderRadius: "16px",
      boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
      border: "1px solid rgba(75,108,183,0.15)",
      p: 0,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
  }}
>
  {/* Header */}
  <Box sx={{
    px: 2.5, py: 2,
    background: "linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexShrink: 0,
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <NotificationsIcon sx={{ color: "#fff", fontSize: 20 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#fff", fontSize: "1rem" }}>
        Notifications
      </Typography>
      {unreadCount > 0 && (
        <Chip
          label={unreadCount}
          size="small"
          sx={{ bgcolor: "#f44336", color: "#fff", fontWeight: 700, height: 20, fontSize: "0.7rem", ml: 0.5 }}
        />
      )}
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {unreadCount > 0 && (
        <Tooltip title="Mark all as read">
          <IconButton size="small" onClick={markAllAsRead} disabled={markingAll}
            sx={{ color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}>
            {markingAll
              ? <CircularProgress size={14} sx={{ color: "#fff" }} />
              : <DoneAllIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
      <IconButton size="small" onClick={handleCloseNotifications}
        sx={{ color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  </Box>

  {/* Scrollable list */}
  <Box
    ref={notifListRef}
    sx={{
      flex: 1,
      overflowY: "auto",
      maxHeight: 380,
      "&::-webkit-scrollbar": { width: 5 },
      "&::-webkit-scrollbar-thumb": { background: "#b5c3e8", borderRadius: 3 },
    }}
    onScroll={async (e) => {
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 60 && hasMoreRef.current && !loadingMoreNotifs) {
        setLoadingMoreNotifs(true);
        const next = notifPageNoRef.current + 1;
        notifPageNoRef.current = next;
        setNotifPageNo(next);
        await fetchNotifications(next, true);
        setLoadingMoreNotifs(false);
      }
    }}
  >
    {notifications.length === 0 ? (
      <Box sx={{ textAlign: "center", py: 6, px: 3 }}>
        <NotificationsIcon sx={{ fontSize: 48, color: "#e0e0e0", mb: 1 }} />
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          You're all caught up!
        </Typography>
        <Typography variant="caption" color="text.disabled">
          No notifications yet.
        </Typography>
      </Box>
    ) : (
      notifications.map((notif, index) => {
        const typeColors = { Reminder: "#ff9800", Success: "#4caf50", Alert: "#f44336", Info: "#2196f3" };
        const dotColor = typeColors[notif.notificationType] || "#4b6cb7";
        const initial = (notif.title || notif.name || "").charAt(0).toUpperCase() || "🔔";
        return (
          <Box
            key={notif.recipientId}
            onClick={() => { if (!notif.isRead) handleMarkAsRead(notif.recipientId); }}
            sx={{
              display: "flex", gap: 1.5, px: 2, py: 1.5,
              cursor: notif.isRead ? "default" : "pointer",
              bgcolor: !notif.isRead ? "rgba(75,108,183,0.06)" : "transparent",
              borderLeft: !notif.isRead ? "3px solid rgba(75,108,183,0.6)" : "3px solid transparent",
              borderBottom: index < notifications.length - 1 ? "1px solid #f0f0f0" : "none",
              transition: "background 0.15s",
              "&:hover": { bgcolor: !notif.isRead ? "rgba(75,108,183,0.12)" : "rgba(0,0,0,0.03)" },
            }}
          >
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar sx={{
                width: 40, height: 40, fontWeight: 700, fontSize: "1rem",
                background: "linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)",
              }}>
                {initial}
              </Avatar>
              <Box sx={{
                position: "absolute", bottom: 0, right: 0,
                width: 11, height: 11, borderRadius: "50%",
                bgcolor: dotColor, border: "2px solid #fff",
              }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Typography variant="subtitle2"
                  fontWeight={notif.isRead ? 500 : 700}
                  color={notif.isRead ? "text.secondary" : "text.primary"}
                  sx={{ lineHeight: 1.3, flex: 1, pr: 1 }}
                  noWrap
                >
                  {notif.title || notif.name}
                </Typography>
                <Typography variant="caption" color="text.disabled"
                  sx={{ whiteSpace: "nowrap", fontSize: "0.68rem", mt: 0.2 }}>
                  {notif.createdOn
                    ? new Date(notif.createdOn).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                    : "Just now"}
                </Typography>
              </Box>

              <Tooltip title={notif.message || ""} placement="left" arrow
                componentsProps={{
                  tooltip: { sx: { bgcolor: "#182848", color: "#fff", fontSize: "0.78rem", maxWidth: 280, p: "10px 14px", borderRadius: 2 } },
                  arrow: { sx: { color: "#182848" } },
                }}
              >
                <Typography variant="body2" color="text.secondary"
                  sx={{ fontSize: "0.8rem", lineHeight: 1.4, mt: 0.3,
                    display: "-webkit-box", WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical", overflow: "hidden", cursor: "default" }}
                >
                  {notif.message}
                </Typography>
              </Tooltip>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5 }}>
                {notif.notificationType && (
                  <Chip label={notif.notificationType} size="small"
                    sx={{ height: 16, fontSize: "0.62rem", fontWeight: 600,
                      bgcolor: `${dotColor}18`, color: dotColor, px: 0.5 }} />
                )}
                {!notif.isRead && (
                  <Tooltip title="Mark as read">
                    <IconButton size="small"
                      onClick={e => { e.stopPropagation(); handleMarkAsRead(notif.recipientId); }}
                      sx={{ color: "#4b6cb7", p: 0.5, ml: "auto", "&:hover": { bgcolor: "rgba(75,108,183,0.1)" } }}
                    >
                      <MarkEmailReadIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        );
      })
    )}

    {loadingMoreNotifs && (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} sx={{ color: "#4b6cb7" }} />
      </Box>
    )}
  </Box>

  {/* Footer */}
  {notifications.length > 0 && (
    <Box sx={{
      borderTop: "1px solid #f0f0f0", px: 2, py: 1,
      display: "flex", justifyContent: "center", bgcolor: "grey.50", flexShrink: 0,
    }}>
      <Typography variant="caption"
        sx={{ color: "#4b6cb7", cursor: "pointer", fontWeight: 600, fontSize: "0.78rem",
          "&:hover": { textDecoration: "underline" } }}
        onClick={() => { handleCloseNotifications(); navigate('/parent-notifications'); }}
      >
        View all notifications →
      </Typography>
    </Box>
  )}
</Popover>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={openLogoutDialog}
        onClose={handleCancelLogout}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
            minWidth: "400px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle
          id="logout-dialog-title"
          sx={{
            fontWeight: 700,
            fontSize: "1.3rem",
            color: "rgba(24,40,72,1)",
            pb: 1,
          }}
        >
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="logout-dialog-description"
            sx={{
              color: "rgba(0, 0, 0, 0.7)",
              fontSize: "1rem",
            }}
          >
            Are you sure you want to logout from the parent panel?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCancelLogout}
            sx={{
              color: "rgba(75,108,183,1)",
              fontWeight: 600,
              padding: "8px 24px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "rgba(75,108,183,0.08)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            sx={{
              background: "linear-gradient(110deg, rgba(211,47,47,1) 11.3%, rgba(183,28,28,1) 99.3%)",
              color: "#fff",
              fontWeight: 600,
              padding: "8px 24px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(211, 47, 47, 0.3)",
              "&:hover": {
                background: "linear-gradient(110deg, rgba(183,28,28,1) 11.3%, rgba(211,47,47,1) 99.3%)",
                boxShadow: "0 6px 16px rgba(211, 47, 47, 0.4)",
              },
            }}
            autoFocus
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default Header;
